import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function FlutterRush({ user, profile, onNavigate }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const settingsRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const gameRef = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!gameOver) return;
    setScoreSaved(false);
    supabase.from('heartrush_leaderboard').insert([{
      user_id: user.id,
      score: gameRef.current.score,
      username: profile?.username || null,
      email: user.email,
    }]).then(({ error }) => {
      if (error) console.error('Error saving score:', error);
      setScoreSaved(true);
    });
  }, [gameOver, user, profile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    gameRef.current = {
      started: false,
      over: false,
      score: 0,
      animationId: null,
      flapAngle: 0,
      deathAnim: null,
      tickCount: 0,
      speed: 4,
      bird: { x: 80, y: 0, velocity: 0, isGrounded: false, jumpsLeft: 2 },
      obstacles: [],
      collectibles: [],
      ledges: [],
      obstacleTimer: 0,
      collectibleTimer: 0,
      ledgeTimer: 0,
      nextLedge: 80,
      lastLedgeH: 100,
      bgOffset: 0,
      groundY: 0,
    };

    const ctx = canvas.getContext('2d');
    const game = gameRef.current;

    const GRAVITY = 0.6;
    const JUMP = -13;
    const BIRD_SIZE = 22;
    const GROUND_H = 60;
    game.groundY = canvas.height - GROUND_H;
    game.bird.y = game.groundY - BIRD_SIZE;

    // ── Obstacle types ──────────────────────────────────────
    const OBSTACLE_TYPES = [
      {
        type: 'thorn',
        width: 28,
        height: 40,
        draw: (ctx, x, y) => {
          // Spike cluster
          ctx.fillStyle = '#2d6a2d';
          const spikes = 5;
          const sw = 28 / spikes;
          for (let i = 0; i < spikes; i++) {
            ctx.beginPath();
            ctx.moveTo(x + i * sw, y + 40);
            ctx.lineTo(x + i * sw + sw / 2, y);
            ctx.lineTo(x + i * sw + sw, y + 40);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = '#3d8a3d';
          }
          // Base
          ctx.fillStyle = '#1a4a1a';
          ctx.fillRect(x, y + 32, 28, 8);
        }
      },
      {
        type: 'brokenheart',
        width: 36,
        height: 36,
        draw: (ctx, x, y) => {
          ctx.save();
          ctx.translate(x + 18, y + 16);
          ctx.fillStyle = '#e11d48';

          const drawBrokenHeartPath = (size) => {
            ctx.moveTo(0, size * 0.4);
            ctx.bezierCurveTo(-size * 0.1, size * 0.2, -size * 0.65, -size * 0.1, -size * 0.65, -size * 0.15);
            ctx.bezierCurveTo(-size * 0.65, -size * 0.55, -size * 0.1, -size * 0.65, 0, -size * 0.35);
            ctx.bezierCurveTo(size * 0.1, -size * 0.65, size * 0.65, -size * 0.55, size * 0.65, -size * 0.15);
            ctx.bezierCurveTo(size * 0.65, -size * 0.1, size * 0.1, size * 0.2, 0, size * 0.4);
            ctx.closePath();
          };

          const size = 18;
          const zz = [
            { x: size * 0.05,  y: -size * 0.65 },
            { x: -size * 0.12, y: -size * 0.35 },
            { x: size * 0.1,   y: -size * 0.1  },
            { x: -size * 0.08, y:  size * 0.15 },
            { x: size * 0.06,  y:  size * 0.4  },
          ];

          // Left half
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(-size * 2, zz[zz.length - 1].y);
          ctx.lineTo(-size * 2, -size * 2);
          ctx.lineTo(zz[0].x, -size * 2);
          ctx.closePath();
          ctx.clip();
          ctx.fillStyle = '#e11d48';
          ctx.beginPath();
          drawBrokenHeartPath(size);
          ctx.fill();
          ctx.restore();

          // Right half (slightly offset)
          ctx.save();
          ctx.translate(4, 2);
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(size * 2, zz[zz.length - 1].y);
          ctx.lineTo(size * 2, -size * 2);
          ctx.lineTo(zz[0].x, -size * 2);
          ctx.closePath();
          ctx.clip();
          ctx.fillStyle = '#be123c';
          ctx.beginPath();
          drawBrokenHeartPath(size);
          ctx.fill();
          ctx.restore();

          ctx.restore();
        }
      },
      {
        type: 'cloud',
        width: 70,
        height: 50,
        draw: (ctx, x, y) => {
          ctx.fillStyle = '#94a3b8';
          ctx.shadowColor = 'rgba(100,100,150,0.3)';
          ctx.shadowBlur = 8;
          const puffs = [
            { dx: 35, dy: 35, r: 18 },
            { dx: 55, dy: 28, r: 14 },
            { dx: 18, dy: 30, r: 13 },
            { dx: 40, dy: 18, r: 13 },
          ];
          puffs.forEach(p => {
            ctx.beginPath();
            ctx.arc(x + p.dx, y + p.dy, p.r, 0, Math.PI * 2);
            ctx.fill();
          });
          ctx.shadowBlur = 0;
          // Lightning bolt
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.moveTo(x + 38, y + 44);
          ctx.lineTo(x + 44, y + 30);
          ctx.lineTo(x + 40, y + 30);
          ctx.lineTo(x + 46, y + 16);
          ctx.lineTo(x + 34, y + 33);
          ctx.lineTo(x + 38, y + 33);
          ctx.closePath();
          ctx.fill();
        }
      },
      {
        type: 'tall_cloud',
        width: 95,
        height: 110,
        floatY: 160, // top of cloud sits 160px above groundY — safe to run under, dangerous to jump into
        draw: (ctx, x, y) => {
          ctx.fillStyle = '#64748b';
          ctx.shadowColor = 'rgba(60,60,120,0.4)';
          ctx.shadowBlur = 14;
          const puffs = [
            { dx: 48, dy: 55, r: 28 },
            { dx: 75, dy: 42, r: 22 },
            { dx: 22, dy: 48, r: 20 },
            { dx: 58, dy: 28, r: 22 },
            { dx: 30, dy: 30, r: 19 },
            { dx: 70, dy: 65, r: 18 },
            { dx: 20, dy: 65, r: 17 },
          ];
          puffs.forEach(p => {
            ctx.beginPath();
            ctx.arc(x + p.dx, y + p.dy, p.r, 0, Math.PI * 2);
            ctx.fill();
          });
          ctx.shadowBlur = 0;
          // Two lightning bolts
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.moveTo(x + 44, y + 100);
          ctx.lineTo(x + 52, y + 78);
          ctx.lineTo(x + 47, y + 78);
          ctx.lineTo(x + 55, y + 58);
          ctx.lineTo(x + 40, y + 82);
          ctx.lineTo(x + 45, y + 82);
          ctx.closePath();
          ctx.fill();
          ctx.beginPath();
          ctx.moveTo(x + 66, y + 95);
          ctx.lineTo(x + 72, y + 78);
          ctx.lineTo(x + 68, y + 78);
          ctx.lineTo(x + 74, y + 63);
          ctx.lineTo(x + 62, y + 82);
          ctx.lineTo(x + 66, y + 82);
          ctx.closePath();
          ctx.fill();
          // Rain streaks
          ctx.strokeStyle = 'rgba(147,197,253,0.7)';
          ctx.lineWidth = 1.5;
          ctx.lineCap = 'round';
          [[28, 90, 22, 108], [50, 95, 44, 110], [72, 88, 66, 106]].forEach(([x1, y1, x2, y2]) => {
            ctx.beginPath();
            ctx.moveTo(x + x1, y + y1);
            ctx.lineTo(x + x2, y + y2);
            ctx.stroke();
          });
        }
      },
      {
        type: 'rose',
        width: 32,
        height: 58,
        draw: (ctx, x, y) => {
          const cx = x + 16;

          // Stem
          ctx.strokeStyle = '#15803d';
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(cx, y + 58);
          ctx.lineTo(cx, y + 22);
          ctx.stroke();

          // Left leaf
          ctx.fillStyle = '#22c55e';
          ctx.beginPath();
          ctx.moveTo(cx, y + 42);
          ctx.bezierCurveTo(cx - 15, y + 32, cx - 17, y + 46, cx - 8, y + 48);
          ctx.bezierCurveTo(cx - 3, y + 50, cx, y + 44, cx, y + 42);
          ctx.fill();

          // Right leaf
          ctx.beginPath();
          ctx.moveTo(cx, y + 32);
          ctx.bezierCurveTo(cx + 15, y + 22, cx + 17, y + 36, cx + 8, y + 38);
          ctx.bezierCurveTo(cx + 3, y + 40, cx, y + 34, cx, y + 32);
          ctx.fill();

          // Petals
          const bloomCX = cx;
          const bloomCY = y + 14;
          ctx.fillStyle = '#fb7185';
          for (let i = 0; i < 5; i++) {
            const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
            ctx.beginPath();
            ctx.arc(bloomCX + Math.cos(angle) * 7, bloomCY + Math.sin(angle) * 7, 9, 0, Math.PI * 2);
            ctx.fill();
          }

          // Bloom centre
          ctx.fillStyle = '#f43f5e';
          ctx.beginPath();
          ctx.arc(bloomCX, bloomCY, 7, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#be123c';
          ctx.beginPath();
          ctx.arc(bloomCX, bloomCY, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      },
      {
        type: 'small_cloud',
        width: 52,
        height: 38,
        draw: (ctx, x, y) => {
          ctx.fillStyle = '#94a3b8';
          ctx.shadowColor = 'rgba(100,100,150,0.3)';
          ctx.shadowBlur = 6;
          [{ dx: 26, dy: 26, r: 14 }, { dx: 40, dy: 20, r: 11 },
           { dx: 13, dy: 22, r: 10 }, { dx: 28, dy: 14, r: 10 }].forEach(p => {
            ctx.beginPath();
            ctx.arc(x + p.dx, y + p.dy, p.r, 0, Math.PI * 2);
            ctx.fill();
          });
          ctx.shadowBlur = 0;
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.moveTo(x + 28, y + 32);
          ctx.lineTo(x + 32, y + 20);
          ctx.lineTo(x + 29, y + 20);
          ctx.lineTo(x + 33, y + 10);
          ctx.lineTo(x + 24, y + 23);
          ctx.lineTo(x + 27, y + 23);
          ctx.closePath();
          ctx.fill();
        }
      },
      {
        type: 'flower_pot',
        width: 26,
        height: 34,
        draw: (ctx, x, y) => {
          // Pot body (trapezoid)
          ctx.fillStyle = '#c2410c';
          ctx.beginPath();
          ctx.moveTo(x + 3,  y + 14);
          ctx.lineTo(x + 23, y + 14);
          ctx.lineTo(x + 26, y + 34);
          ctx.lineTo(x,      y + 34);
          ctx.closePath();
          ctx.fill();
          // Rim
          ctx.fillStyle = '#ea580c';
          ctx.fillRect(x, y + 11, 26, 6);
          // Stem
          ctx.strokeStyle = '#16a34a';
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(x + 13, y + 11);
          ctx.lineTo(x + 13, y + 1);
          ctx.stroke();
          // Petals
          ctx.fillStyle = '#fb7185';
          for (let i = 0; i < 5; i++) {
            const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
            ctx.beginPath();
            ctx.arc(x + 13 + Math.cos(a) * 6, y + 1 + Math.sin(a) * 6, 5, 0, Math.PI * 2);
            ctx.fill();
          }
          // Centre
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(x + 13, y + 1, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      },
    ];

    // ── Helpers ─────────────────────────────────────────────
    const drawHeartPath = (size) => {
      ctx.moveTo(0, size * 0.4);
      ctx.bezierCurveTo(-size * 0.1, size * 0.2, -size * 0.65, -size * 0.1, -size * 0.65, -size * 0.15);
      ctx.bezierCurveTo(-size * 0.65, -size * 0.55, -size * 0.1, -size * 0.65, 0, -size * 0.35);
      ctx.bezierCurveTo(size * 0.1, -size * 0.65, size * 0.65, -size * 0.55, size * 0.65, -size * 0.15);
      ctx.bezierCurveTo(size * 0.65, -size * 0.1, size * 0.1, size * 0.2, 0, size * 0.4);
      ctx.closePath();
    };

    const getZigZag = (size) => [
      { x: size * 0.05,  y: -size * 0.65 },
      { x: -size * 0.12, y: -size * 0.35 },
      { x: size * 0.1,   y: -size * 0.1  },
      { x: -size * 0.08, y:  size * 0.15 },
      { x: size * 0.06,  y:  size * 0.4  },
    ];

    const spawnObstacle = () => {
    const type = OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)];

    let y;

    if (type.type === 'tall_cloud' || type.type === 'cloud' || type.type === 'small_cloud') {
        // Smooth random cloud height instead of fixed floatY
        const minFloatY = 140;
        const maxFloatY = 420;
        const chosenFloatY =
        minFloatY + Math.floor(Math.random() * (maxFloatY - minFloatY + 1));

        y = game.groundY - chosenFloatY;
    } else {
        // Ground obstacles stay on the ground
        y = game.groundY - type.height;
    }

    game.obstacles.push({
        ...type,
        x: canvas.width + 20,
        y,
    });
    };

    const spawnLedge = () => {
      // 100px gaps: single jump (~141px) clears one level, double jump (~282px) clears two
      const LEVELS = [100, 200, 300, 400];
      const lastIdx = LEVELS.indexOf(game.lastLedgeH);
      const startIdx = lastIdx === -1 ? 0 : lastIdx;
      // Step up, stay, or step down — max 2 levels at once (200px = within double-jump range)
      const delta = [-2, -1, 0, 0, 1, 2][Math.floor(Math.random() * 6)];
      const nextIdx = Math.max(0, Math.min(LEVELS.length - 1, startIdx + delta));
      const aboveGround = LEVELS[nextIdx];
      game.lastLedgeH = aboveGround;

      // Long ledges — gives the player time to land and react
      const width = 200 + Math.floor(Math.random() * 180);
      const ledge = {
        x: canvas.width + 20,
        y: game.groundY - aboveGround,
        width,
        height: 14,
      };
      game.ledges.push(ledge);

      
      // Place flower pots on top of wider ledges
      // Place ledge-safe obstacles on top of wider ledges
    if (Math.random() < 0.15 && width >= 60) {
        const maxObstacles = width >= 120 ? 2 : 1;
        const numObstacles = Math.ceil(Math.random() * maxObstacles);
        const placed = [];

        for (let i = 0; i < numObstacles; i++) {
            const obstacleType =
            OBSTACLE_TYPES[Math.floor(Math.random() * OBSTACLE_TYPES.length)]
            const obstacleX =
            ledge.x + 6 + Math.floor(Math.random() * (width - obstacleType.width - 12));

            const overlaps = placed.some(p =>
            obstacleX < p.x + p.width + 8 &&
            obstacleX + obstacleType.width + 8 > p.x
            );

            if (!overlaps) {
            placed.push({ x: obstacleX, width: obstacleType.width });

            game.obstacles.push({
                ...obstacleType,
                x: obstacleX,
                y: ledge.y - obstacleType.height,
            });
            }
        }
        }
    };

    const drawLedge = (ledge) => {
      // Body
      ctx.fillStyle = '#f9a8d4';
      ctx.fillRect(ledge.x, ledge.y, ledge.width, ledge.height);
      // Top stripe
      ctx.fillStyle = '#f472b6';
      ctx.fillRect(ledge.x, ledge.y, ledge.width, 5);
      // Support pillars
      ctx.fillStyle = '#fda4af';
      [[10, 18], [ledge.width - 18, 18]].forEach(([dx, pw]) => {
        ctx.fillRect(ledge.x + dx, ledge.y + ledge.height, pw - 10, 22);
      });
    };

    const spawnCollectible = () => {
      const heights = [
        game.groundY - BIRD_SIZE * 2,
        game.groundY - BIRD_SIZE * 4,
        game.groundY - BIRD_SIZE * 6,
      ];
      game.collectibles.push({
        x: canvas.width + 20,
        y: heights[Math.floor(Math.random() * heights.length)],
        size: 10,
        collected: false,
        bobOffset: Math.random() * Math.PI * 2,
      });
    };

    // ── Render ───────────────────────────────────────────────
    const render = () => {
      // Sky gradient
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#fce4f5');
      grad.addColorStop(0.7, '#fef0ff');
      grad.addColorStop(1, '#f8d7ea');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Scrolling ground
      game.bgOffset = (game.bgOffset + game.speed) % 40;
      // Ground base
      ctx.fillStyle = '#f9a8d4';
      ctx.fillRect(0, game.groundY, canvas.width, GROUND_H);
      // Ground stripe
      ctx.fillStyle = '#f472b6';
      ctx.fillRect(0, game.groundY, canvas.width, 6);
      // Ground dashes
      ctx.fillStyle = '#fda4af';
      for (let x = -game.bgOffset; x < canvas.width; x += 40) {
        ctx.fillRect(x, game.groundY + 18, 24, 4);
      }

      // Collectibles
      game.collectibles.forEach(c => {
        if (c.collected) return;
        const bob = Math.sin(game.tickCount * 0.08 + c.bobOffset) * 4;
        ctx.save();
        ctx.translate(c.x, c.y + bob);
        ctx.fillStyle = '#fb7185';
        ctx.shadowColor = 'rgba(251,113,133,0.5)';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        drawHeartPath(c.size);
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.restore();
      });

      // Ledges
      game.ledges.forEach(ledge => drawLedge(ledge));

      // Obstacles
      game.obstacles.forEach(obs => {
        obs.draw(ctx, obs.x, obs.y);
      });

      // Heart character
      const hx = game.bird.x + BIRD_SIZE / 2;
      const hy = game.bird.y + BIRD_SIZE / 2;
      const s = BIRD_SIZE * 0.62;
      const tilt = Math.min(Math.max(game.bird.velocity * 0.03, -0.3), 0.5);
      game.flapAngle += game.bird.isGrounded ? 0.04 : 0.18;
      const flap = Math.sin(game.flapAngle) * s * (game.bird.isGrounded ? 0.15 : 0.5);

      ctx.save();
      ctx.translate(hx, hy);
      ctx.rotate(tilt);

      const drawWing = (side) => {
        const d = side;
        const wGrad = ctx.createLinearGradient(d * s * 0.3, 0, d * s * 2.2, 0);
        wGrad.addColorStop(0, 'rgba(255,220,240,1)');
        wGrad.addColorStop(0.5, 'rgba(255,240,250,0.97)');
        wGrad.addColorStop(1, 'rgba(255,255,255,0.85)');
        ctx.fillStyle = wGrad;
        ctx.beginPath();
        ctx.moveTo(d * s * 0.28, s * 0.05);
        ctx.bezierCurveTo(d * s * 0.9, -s * 0.35 + flap, d * s * 2.05, -s * 0.15 + flap, d * s * 2.2, s * 0.28 + flap * 0.3);
        ctx.bezierCurveTo(d * s * 1.7, s * 0.55, d * s * 0.75, s * 0.38, d * s * 0.28, s * 0.28);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(230,170,210,0.55)';
        ctx.lineWidth = 1;
        ctx.stroke();
      };

      if (!game.deathAnim) {
        drawWing(-1);
        drawWing(1);
      }

      const heartSize = BIRD_SIZE * 1.3;
      if (!game.deathAnim) {
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
      } else {
        const t = game.deathAnim.frame / 40;
        const spread = t * s * 2.5;
        const drop = t * t * s * 3;
        const spin = t * 1.0;
        const alpha = Math.max(0, 1 - t);
        const zz = getZigZag(heartSize);
        ctx.globalAlpha = alpha;

        const leftClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(-heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(-heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };

        const rightClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };

        ctx.save();
        ctx.translate(-spread, drop);
        ctx.rotate(-spin);
        leftClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.translate(spread, drop);
        ctx.rotate(spin);
        rightClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.globalAlpha = 1;
      }

      ctx.restore();

      // Shockwave on death
      if (game.deathAnim && game.deathAnim.frame < 20) {
        const t = game.deathAnim.frame / 20;
        ctx.save();
        ctx.translate(game.deathAnim.x, game.deathAnim.y);
        ctx.strokeStyle = `rgba(244,63,94,${1 - t})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, t * BIRD_SIZE * 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }

      // Score
      ctx.fillStyle = 'rgba(180,80,120,0.75)';
      ctx.font = 'bold 22px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(game.score, canvas.width / 2, 36);
      ctx.textAlign = 'left';

      // Start message
      if (!game.started) {
        ctx.fillStyle = 'rgba(255,240,250,0.65)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#a03070';
        ctx.font = '600 16px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Click or press Space to jump', canvas.width / 2, canvas.height / 2);
        ctx.textAlign = 'left';
      }
    };

    // ── Update ───────────────────────────────────────────────
    const update = () => {
      if (!game.started || game.over) return;
      game.tickCount++;

      // Gradually increase speed
      game.speed = 4 + Math.floor(game.tickCount / 300) * 0.5;

      // Physics
      game.bird.velocity += GRAVITY;
      game.bird.y += game.bird.velocity;

      // Ground collision
      if (game.bird.y >= game.groundY - BIRD_SIZE) {
        game.bird.y = game.groundY - BIRD_SIZE;
        game.bird.velocity = 0;
        game.bird.isGrounded = true;
        game.bird.jumpsLeft = 2;
      } else {
        game.bird.isGrounded = false;
        // Ledge landing — only snap when falling downward
        if (game.bird.velocity > 0) {
          for (const ledge of game.ledges) {
            const onX = game.bird.x + BIRD_SIZE - 4 > ledge.x && game.bird.x + 4 < ledge.x + ledge.width;
            const birdBottom = game.bird.y + BIRD_SIZE;
            if (onX && birdBottom >= ledge.y && birdBottom <= ledge.y + ledge.height + 8) {
              game.bird.y = ledge.y - BIRD_SIZE;
              game.bird.velocity = 0;
              game.bird.isGrounded = true;
              game.bird.jumpsLeft = 2;
              break;
            }
          }
        }
      }

      // Ceiling
      if (game.bird.y < 0) {
        game.bird.y = 0;
        game.bird.velocity = 0;
      }

      // Move obstacles
      game.obstacles.forEach(obs => { obs.x -= game.speed; });
      game.obstacles = game.obstacles.filter(o => o.x > -100);

      // Move collectibles
      game.collectibles.forEach(c => { c.x -= game.speed; });
      game.collectibles = game.collectibles.filter(c => c.x > -50 && !c.collected);

      // Move ledges
      game.ledges.forEach(l => { l.x -= game.speed; });
      game.ledges = game.ledges.filter(l => l.x + l.width > -10);

      // Spawn obstacles
      game.obstacleTimer++;
      const spawnInterval = Math.max(60, 110 - Math.floor(game.tickCount / 200) * 5);
      if (game.obstacleTimer > spawnInterval) {
        spawnObstacle();
        game.obstacleTimer = 0;
      }

      // Spawn collectibles
      game.collectibleTimer++;
      if (game.collectibleTimer > 80) {
        if (Math.random() < 0.5) spawnCollectible();
        game.collectibleTimer = 0;
      }

      // Spawn ledges — gap kept at ~80-160px regardless of speed
      game.ledgeTimer++;
      if (game.ledgeTimer > game.nextLedge) {
        spawnLedge();
        game.ledgeTimer = 0;
        const gapPx = 80 + Math.floor(Math.random() * 80);
        game.nextLedge = Math.max(30, Math.floor(gapPx / game.speed));
      }

      // Collision — obstacles
      const birdLeft   = game.bird.x + 4;
      const birdRight  = game.bird.x + BIRD_SIZE - 4;
      const birdTop    = game.bird.y + 4;
      const birdBottom = game.bird.y + BIRD_SIZE - 4;

      for (const obs of game.obstacles) {
        if (
          birdRight  > obs.x + 4 &&
          birdLeft   < obs.x + obs.width - 4 &&
          birdBottom > obs.y + 4 &&
          birdTop    < obs.y + obs.height
        ) {
          game.over = true;
          game.deathAnim = { frame: 0, x: game.bird.x + BIRD_SIZE / 2, y: game.bird.y + BIRD_SIZE / 2 };
          return;
        }
      }

      // Collision — collectibles
      game.collectibles.forEach(c => {
        if (c.collected) return;
        const dx = (game.bird.x + BIRD_SIZE / 2) - c.x;
        const dy = (game.bird.y + BIRD_SIZE / 2) - c.y;
        if (Math.sqrt(dx * dx + dy * dy) < BIRD_SIZE) {
          c.collected = true;
          game.score += 2;
          setScore(game.score);
        }
      });

      // Score over time
      if (game.tickCount % 60 === 0) {
        game.score += 1;
        setScore(game.score);
      }
    };

    const DEATH_FRAMES = 40;

    const gameLoop = () => {
      update();
      render();
      if (!game.over) {
        game.animationId = requestAnimationFrame(gameLoop);
      } else if (game.deathAnim && game.deathAnim.frame <= DEATH_FRAMES) {
        game.deathAnim.frame++;
        render();
        if (game.deathAnim.frame <= DEATH_FRAMES) {
          game.animationId = requestAnimationFrame(gameLoop);
        } else {
          setGameOver(true);
        }
      } else {
        setGameOver(true);
      }
    };

    const jump = () => {
      if (!game.started) {
        game.started = true;
        return;
      }
      if (!game.over && game.bird.jumpsLeft > 0) {
        game.bird.velocity = JUMP;
        game.bird.isGrounded = false;
        game.bird.jumpsLeft--;
      }
    };

    const handleKeyDown = (e) => { if (e.code === 'Space') { e.preventDefault(); jump(); } };
    const handleClick = () => jump();
    const handleTouch = (e) => { e.preventDefault(); jump(); };

    render();
    gameLoop();
    window.addEventListener('keydown', handleKeyDown);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouch, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouch);
      cancelAnimationFrame(game.animationId);
    };
  }, [restartTrigger]);

  const handlePlayAgain = () => {
    setScore(0);
    setGameOver(false);
    setRestartTrigger(t => t + 1);
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="settings-menu" ref={settingsRef}>
          <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
          {settingsOpen && (
            <div className="settings-dropdown">
              <div className="settings-email">{user.email}</div>
              <hr className="settings-divider" />
              <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
              {profile?.is_admin && (
                <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
              )}
              <hr className="settings-divider" />
              <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
            </div>
          )}
        </div>
        <span className="game-username">{profile?.username || user.email}</span>
        <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
      </div>

      <h1 className="game-title">Flutter Rush🏃</h1>
      <canvas ref={canvasRef} width={500} height={600} className="game-canvas" />

      <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
        🏆 Leaderboard
      </button>
      <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
        🏅 Leagues
      </button>

      {gameOver && (
        <div className="game-over-modal">
          <div className="game-over-box">
            <h2>Game Over!</h2>
            <p className="final-score">{score}</p>
            <button onClick={handlePlayAgain} className="btn">Play Again</button>
            <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
              {scoreSaved ? 'View Leaderboard' : 'Saving…'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}